import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { useAlertStore } from "@/stores/alert";
import { filterEmptyValues } from "@/utils/converter";
import { REFRESH_TOKEN_URL } from "@/constants/auth";

declare module "axios" {
  export interface AxiosRequestConfig {
    _silentRefresh?: boolean;
    _retry?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    metadata?: { requestId: string };
    _retry?: boolean;
    _silentRefresh?: boolean;
  }
}

interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
  errorCode?: string;
  timestamp: string;
}

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

class ApiService {
  private axiosInstance: AxiosInstance;
  private pendingRequests: Map<string, AbortController> = new Map();
  private isShowingTokenExpiredAlert = false;
  private isRefreshing = false;
  private failedQueue: QueueItem[] = [];

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 90000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      withCredentials: false
    });

    this.setupInterceptors();
  }

  // HTTP 메소드들
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.get(url, config);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.post(url, data, config);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.put(url, data, config);
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.patch(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.delete(url, config);
  }

  // 파일 업로드 전용 메소드
  async uploadFile<T>(url: string, file: File, additionalData?: any): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.keys(additionalData).forEach((key) => {
        formData.append(key, additionalData[key]);
      });
    }

    return this.axiosInstance.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  // 다중 파일 업로드 메소드
  async uploadFiles<T>(url: string, files: File[], additionalData?: any): Promise<ApiResponse<T>> {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    if (additionalData) {
      Object.keys(additionalData).forEach((key) => {
        formData.append(key, additionalData[key]);
      });
    }

    return this.axiosInstance.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  // 로컬 Blob/File 다운로드 메소드 (서버 요청 없이 클라이언트 측 파일을 저장)
  downloadBlob(blob: Blob, filename: string): void {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // 파일 다운로드 메소드
  async downloadFile(url: string, defaultFilename?: string): Promise<void> {
    const response = await this.axiosInstance.get(url, {
      responseType: "blob"
    });

    // response.data가 이미 Blob인지 확인
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);

    // Content-Type 확인: 파일이 아닌 경우(JSON, HTML, XML 등) 에러 처리
    const contentType = response.headers?.["content-type"] || blob.type || "";
    if (
      (contentType.includes("application/json") ||
        contentType.includes("text/html") ||
        contentType.includes("text/xml")) &&
      blob.size === 0
    ) {
      throw new Error("파일을 찾을 수 없습니다");
    }

    // JSON 에러 응답 감지 (서버가 200 + application/json으로 에러를 반환하는 경우)
    if (blob.type === "application/json" || (contentType.includes("application/json") && blob.size > 0)) {
      try {
        const text = await blob.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || "파일 다운로드에 실패했습니다");
      } catch (e) {
        if (e instanceof SyntaxError) {
          // JSON 파싱 실패 → 정상 파일로 간주하고 계속 진행
        } else {
          throw e;
        }
      }
    }

    let filename = defaultFilename || "download";

    if (response.headers) {
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const starMatch = contentDisposition.match(/filename\*\s*=\s*[^']*'[^']*'(.+?)(?:\s*;|$)/i);
        if (starMatch?.[1]) {
          try {
            filename = decodeURIComponent(starMatch[1].trim());
          } catch {
            filename = starMatch[1].replace(/%20/g, " ").trim();
          }
        } else {
          const filenameMatch = contentDisposition.match(/filename\s*=((['"]).*?\2|[^;\n]*)/i);
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
            try {
              filename = filename.replace(/\+/g, " ");
              filename = decodeURIComponent(filename);
            } catch {
              filename = filename.replace(/\+/g, " ");
            }
          }
        }
      }
    }

    this.downloadBlob(blob, filename);
  }

  // Excel 파일 다운로드 메소드
  async excelDownloadFile(url: string, params?: Record<string, any>, filenamePrefix?: string): Promise<void> {
    const filteredParams = params ? filterEmptyValues(params) : undefined;

    if (filteredParams) {
      delete filteredParams.page;
      delete filteredParams.size;
    }

    const response = await this.axiosInstance.get(url, {
      params: filteredParams,
      responseType: "blob",
      validateStatus: () => true
    });

    if (response.status >= 400) {
      let errorMessage = "엑셀 다운로드에 실패했습니다";

      if (response.data instanceof Blob) {
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // JSON 파싱 실패 시 기본 메시지 사용
        }
      }

      throw new Error(errorMessage);
    }

    if (response.data instanceof Blob && response.data.type === "application/json") {
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.message || "엑셀 다운로드에 실패했습니다");
    }

    let filename = "";

    if (response.headers) {
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const starMatch = contentDisposition.match(/filename\*\s*=\s*[^']*'[^']*'(.+?)(?:\s*;|$)/i);
        if (starMatch?.[1]) {
          try {
            filename = decodeURIComponent(starMatch[1].trim());
          } catch {
            filename = starMatch[1].replace(/%20/g, " ").trim();
          }
        } else {
          const filenameMatch = contentDisposition.match(/filename\s*=((['"]).*?\2|[^;\n]*)/i);
          if (filenameMatch?.[1]) {
            let serverFilename = filenameMatch[1].replace(/['"]/g, "");
            try {
              serverFilename = serverFilename.replace(/\+/g, " ");
              filename = decodeURIComponent(serverFilename);
            } catch {
              filename = serverFilename.replace(/\+/g, " ");
            }
          }
        }
      }
    }

    if (!filename) {
      const prefix = filenamePrefix || "download";
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const dateStr = `${year}${month}${day}`;
      filename = `${prefix}_${dateStr}.xlsx`;
    }

    const blob = new Blob([response.data as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    this.downloadBlob(blob, filename);
  }

  // 요청 취소를 위한 AbortController 생성
  createCancelToken(): AbortController {
    return new AbortController();
  }

  // 모든 진행 중인 요청 취소 (기본: /auth/refresh 요청은 제외, 로그아웃 시 전체 취소)
  cancelAllRequests(reason = "Navigation", includeRefresh = false): void {
    this.pendingRequests.forEach((controller, requestId) => {
      if (!includeRefresh && requestId.includes(REFRESH_TOKEN_URL)) {
        return;
      }
      if (import.meta.env.DEV) {
        console.log(`[API Cancel] Cancelling request: ${requestId} (${reason})`);
      }
      controller.abort(reason);
    });
    const keysToDelete: string[] = [];
    this.pendingRequests.forEach((_controller, requestId) => {
      if (includeRefresh || !requestId.includes(REFRESH_TOKEN_URL)) {
        keysToDelete.push(requestId);
      }
    });
    keysToDelete.forEach((key) => this.pendingRequests.delete(key));
  }

  // 특정 요청 취소
  cancelRequest(requestId: string): void {
    const controller = this.pendingRequests.get(requestId);
    if (controller) {
      controller.abort("Manual cancellation");
      this.pendingRequests.delete(requestId);
    }
  }

  // 진행 중인 요청 수 반환
  getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }

  private processQueue(error: any, token: string | null = null): void {
    this.failedQueue.forEach((item) => {
      if (error) {
        item.reject(error);
      } else {
        item.resolve(token!);
      }
    });
    this.failedQueue = [];
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // AbortController가 없으면 자동 생성
        if (!config.signal) {
          const requestId = `${config.method}_${config.url}_${Date.now()}`;
          const abortController = new AbortController();
          config.signal = abortController.signal;
          config.metadata = { requestId };
          this.pendingRequests.set(requestId, abortController);
        }

        // JWT 토큰 추가 (refresh 요청 및 silent refresh 요청에는 만료된 토큰을 보내지 않음)
        if (!config.url?.includes(REFRESH_TOKEN_URL) && !config._silentRefresh) {
          const authStore = useAuthStore();
          const accessToken = authStore.accessToken;
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }

        if (import.meta.env.DEV) {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
        }

        return config;
      },
      (error) => {
        console.error("[API Request Error]", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestId = response.config.metadata?.requestId;
        if (requestId) {
          this.pendingRequests.delete(requestId);
        }

        if (import.meta.env.DEV) {
          console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
        }

        if (response.config.responseType === "blob") {
          return response;
        }

        if (response.data && typeof response.data === "object" && "success" in response.data) {
          return response.data;
        }

        // @deprecated 레거시 응답 형식 — 현재 백엔드는 ApiResponseBodyAdvice로 래핑하므로 도달하지 않아야 함
        if (
          response.data &&
          typeof response.data === "object" &&
          "code" in response.data &&
          "status" in response.data &&
          "message" in response.data
        ) {
          if (import.meta.env.DEV) {
            console.warn("[API] 레거시 응답 형식 감지:", response.config.url);
          }
          return {
            success: response.data.code === 200,
            data: response.data.data || response.data,
            message: response.data.status || response.data.message,
            timestamp: new Date().toISOString()
          };
        }

        // @deprecated 비표준 응답 fallback
        if (import.meta.env.DEV) {
          console.warn("[API] 비표준 응답 형식 감지:", response.config.url);
        }
        return {
          success: true,
          data: response.data,
          message: "Success",
          timestamp: new Date().toISOString()
        };
      },
      async (error) => {
        // 취소된 요청은 조용히 처리 (페이지 이동 등)
        if (axios.isCancel(error)) {
          const cancelledId = error.config?.metadata?.requestId;
          if (cancelledId) {
            this.pendingRequests.delete(cancelledId);
          }
          if (import.meta.env.DEV) {
            console.log(`[API Cancelled] ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
          }
          return Promise.reject({
            success: false,
            cancelled: true,
            data: null,
            message: "Request cancelled",
            errorCode: "REQUEST_CANCELLED",
            timestamp: new Date().toISOString()
          });
        }

        const requestId = error.config?.metadata?.requestId;
        if (requestId) {
          this.pendingRequests.delete(requestId);
        }

        console.error("[API Response Error]", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data
        });

        // 401 Unauthorized 처리 (5단계 + 공개 API 스킵)
        if (error.response?.status === 401) {
          const originalConfig = error.config;

          // 1단계: refresh 요청 자체가 401 → 즉시 중단 (무한 루프 방지)
          if (originalConfig.url?.includes(REFRESH_TOKEN_URL)) {
            this.isRefreshing = false;
            const authStore = useAuthStore();
            authStore.clearSessionState();
            this.processQueue({ success: false, message: "토큰이 만료되었습니다", errorCode: "TOKEN_EXPIRED" }, null);
            // silentRefresh(세션 복원)는 조용히 실패 — 라우터 가드가 로그인 페이지로 유도함
            if (!originalConfig._silentRefresh) {
              this.showTokenExpiredAlert();
            }
            return Promise.reject({
              success: false,
              message: "토큰이 만료되었습니다",
              errorCode: "TOKEN_EXPIRED"
            });
          }

          // 1.5단계: Authorization 헤더 없이 보낸 요청(비인증 공개 API) → 토큰 갱신 불필요
          if (!originalConfig.headers?.Authorization) {
            return Promise.reject({
              success: false,
              data: null,
              message: error.response?.data?.message || "인증이 필요합니다",
              errorCode: error.response?.data?.code || "AUTH_REQUIRED",
              timestamp: new Date().toISOString()
            });
          }

          // 2단계: 이미 재시도한 요청이면 중단
          if (originalConfig._retry) {
            return Promise.reject({
              success: false,
              message: "토큰 갱신 후에도 인증에 실패했습니다",
              errorCode: "TOKEN_RETRY_FAILED"
            });
          }

          // 3단계: 재시도 플래그 설정
          originalConfig._retry = true;

          // 4단계: 이미 refresh 진행 중이면 대기열에 추가 (BUG-4: .then() 체인)
          if (this.isRefreshing) {
            return new Promise<string>((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((newToken) => {
                originalConfig.headers.Authorization = `Bearer ${newToken}`;
                return this.axiosInstance.request(originalConfig);
              })
              .catch((retryError) => {
                return Promise.reject(retryError);
              });
          }

          // 5단계: refresh 시작
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) {
            this.showTokenExpiredAlert();
            return Promise.reject({
              success: false,
              message: "토큰이 만료되었습니다",
              errorCode: "TOKEN_EXPIRED"
            });
          }

          this.isRefreshing = true;
          try {
            const response = (await this.axiosInstance.post(
              REFRESH_TOKEN_URL,
              { refreshToken },
              { _silentRefresh: true }
            )) as ApiResponse<{ accessToken: string; refreshToken: string }>;

            if (response.success) {
              const newAccessToken = response.data.accessToken;
              const newRefreshToken = response.data.refreshToken;

              // MISSING-2: Pinia 스토어 동기화 먼저 (제안-2: 호출 순서 명시)
              const authStore = useAuthStore();
              authStore.setTokens(newAccessToken, newRefreshToken);

              // 대기 중인 요청들 재시도 (새 토큰으로)
              this.processQueue(null, newAccessToken);

              // 원래 요청 재시도
              originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.axiosInstance.request(originalConfig);
            } else {
              const authStore = useAuthStore();
              authStore.clearSessionState();
              this.processQueue({ success: false, message: "토큰이 만료되었습니다", errorCode: "TOKEN_EXPIRED" }, null);
              this.showTokenExpiredAlert();
              return Promise.reject({
                success: false,
                message: "토큰이 만료되었습니다",
                errorCode: "TOKEN_EXPIRED"
              });
            }
          } catch (refreshError) {
            console.error("[Token Refresh Error]", refreshError);
            this.processQueue(refreshError, null);
            this.showTokenExpiredAlert();
            return Promise.reject({
              success: false,
              message: "토큰 갱신에 실패했습니다",
              errorCode: "TOKEN_REFRESH_FAILED"
            });
          } finally {
            this.isRefreshing = false;
          }
        }

        // 403 Forbidden 처리
        if (error.response?.status === 403) {
          return Promise.reject({
            success: false,
            data: null,
            message: error.response?.data?.message || "접근 권한이 없습니다",
            errorCode: error.response?.data?.code || "FORBIDDEN",
            timestamp: new Date().toISOString()
          });
        }

        const isNetworkError = !error.response && !axios.isCancel(error);
        const errorResponse = {
          success: false,
          data: null,
          message: error.response?.data?.message || error.message || "서버 오류가 발생했습니다",
          errorCode: isNetworkError ? "NETWORK_ERROR" : error.response?.data?.code || `HTTP_${error.response?.status}`,
          fieldErrors: error.response?.data?.fieldErrors || null,
          timestamp: new Date().toISOString()
        };

        return Promise.reject(errorResponse);
      }
    );
  }

  private async logout() {
    try {
      const authStore = useAuthStore();
      await authStore.logout();
    } finally {
      // 로그아웃 완료(페이지 이동·요청 취소) 후에 플래그 리셋해야
      // 중간에 도달한 pending 401이 다이얼로그를 재출현시키지 못함
      this.isShowingTokenExpiredAlert = false;
    }
  }

  private showTokenExpiredAlert() {
    if (this.isShowingTokenExpiredAlert) return;

    // 이미 로그인 페이지에 있거나 비관리자 페이지면 다이얼로그 없이 세션만 정리
    // (로그인 페이지에서는 UI 자체가 재로그인을 요구하므로 다이얼로그 중복)
    const pathname = window.location.pathname;
    const isAdminPage = pathname.startsWith("/admin");
    const isLoginPage = pathname === "/admin/login" || pathname === "/login";
    if (!isAdminPage || isLoginPage) {
      const authStore = useAuthStore();
      authStore.clearSessionState();
      return;
    }

    this.isShowingTokenExpiredAlert = true;

    try {
      const alertStore = useAlertStore();
      alertStore.showAlert("토큰이 만료되었습니다.<br>다시 로그인해주세요", () => this.logout());
    } catch {
      console.warn("Alert store not available, falling back to direct logout");
      this.logout();
    }
  }
}

export const apiService = new ApiService();

export default apiService;

export type { ApiResponse };
